import React, { useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faUpload } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { useTranslation } from 'react-i18next';
export interface FileUploadProps {
  accept?: string;

  /** true = mehrere Dateien erlaubt */
  multiple?: boolean;

  /** Optional: max. Anzahl Dateien (nur relevant wenn multiple=true) */
  maxFiles?: number;

  /** Callback mit validierten Dateien */
  onFilesSelected: (files: File[]) => void;

  buttonText?: string;
  titleText?: string;
  uploadIcon?: IconProp;
  dragAndDrop?: boolean;
  dropText?: string;
  hintText?: string;

  disabled?: boolean;
  className?: string;

  /** Optional: rejected files */
  onRejectedFiles?: (rejected: File[]) => void;
}

function matchesAccept(file: File, accept: string): boolean {
  const parts = accept
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length === 0 || parts.includes("*/*")) return true;

  const fileName = file.name.toLowerCase();
  const fileType = (file.type || "").toLowerCase();

  return parts.some((rule) => {
    const r = rule.toLowerCase();

    // ".png" / ".pdf"
    if (r.startsWith(".")) return fileName.endsWith(r);

    // "image/*" / "application/*"
    if (r.endsWith("/*")) {
      const prefix = r.slice(0, -2); // remove "/*"
      return fileType.startsWith(prefix + "/");
    }

    // "image/png" / "application/pdf"
    return fileType === r;
  });
}

const FileUpload= ({
  accept = "*/*",
  multiple = false,
  maxFiles,
  onFilesSelected,
  dragAndDrop = false,
  buttonText = "",
  titleText = '',
  dropText = "",
  hintText = "",
  disabled = false,
  className,
  uploadIcon = faPlus,
  onRejectedFiles,
}: FileUploadProps) => {
  const { t } = useTranslation('imageStudioSettings');
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const acceptLabel = useMemo(() => {
    return !accept || accept === "*/*" ? "Any file type" : accept;
  }, [accept]);

  const openFileDialog = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  const processFiles = (fileList: FileList | null) => {
    if (!fileList) return;

    const all = Array.from(fileList);

    const accepted: File[] = [];
    const rejected: File[] = [];

    for (const f of all) {
      (matchesAccept(f, accept) ? accepted : rejected).push(f);
    }

    if (rejected.length > 0) onRejectedFiles?.(rejected);

    let finalFiles = multiple ? accepted : accepted.slice(0, 1);

    if (multiple && typeof maxFiles === "number") {
      finalFiles = finalFiles.slice(0, Math.max(0, maxFiles));
    }

    if (finalFiles.length > 0) onFilesSelected(finalFiles);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(e.currentTarget.files);
    // wichtig: gleiche Datei nochmal auswählen erlauben
    e.currentTarget.value = "";
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    processFiles(e.dataTransfer.files);
  };

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setIsDragging(true);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const footerInfo = t('file.dragAndDropSub')

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        style={{ display: "none" }}
        onChange={onInputChange}
      />

      <button
        type="button"
        className="btn btn-link text-decoration-none p-0"
        onClick={openFileDialog}
        disabled={disabled}
        style={{ color: "inherit" }}
        title={titleText ?? acceptLabel}
      >
        <FontAwesomeIcon icon={uploadIcon} className="me-2" />
        {buttonText}
      </button>

      {
        dragAndDrop
        ? <div
            className={`mt-2 border rounded p-3 ${isDragging ? "border-primary" : "border-secondary"}`}
            role="button"
            tabIndex={0}
            onClick={openFileDialog}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") openFileDialog();
            }}
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            style={{
              background: "transparent",
              cursor: disabled ? "not-allowed" : "pointer",
              opacity: disabled ? 0.6 : 1,
              userSelect: "none",
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faUpload} />
              <div>
                <div className="fw-semibold">{t('file.dragAndDropUpload')}</div>
                <div className="small text-muted">{footerInfo}</div>
              </div>
            </div>
          </div>
        : <></>
        }
      </div>
  );
};

export default FileUpload;
